import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { AuthContext } from '../../components/AuthContext';
import Select from 'react-select';
import TileSelectionModal from '../../components/TileSelectionModal';
import QRCode from 'qrcode';
import QRCodeSVG from 'react-qr-code';
import { jsPDF } from 'jspdf';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { FaPlus, FaEdit, FaTrash, FaImage, FaTimes, FaSearch, FaChevronLeft, FaChevronRight, FaBoxOpen, FaBox, FaTags, FaBarcode, FaInfoCircle, FaCheck, FaSpinner, FaQrcode, FaDownload, FaFilePdf, FaWarehouse, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const API = process.env.REACT_APP_API_BASE_URL;

const ConceptProductManagement = () => {
  const { user } = useContext(AuthContext);

  // Dark mode
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  useEffect(() => {
    const update = () => {
      const saved = localStorage.getItem('darkMode');
      const sys = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setDarkMode(saved === 'true' || (saved === null && sys));
    };
    update();
    const iv = setInterval(update, 500);
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', update);
    return () => { clearInterval(iv); mq.removeEventListener('change', update); };
  }, []);

  // State
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedTileObjects, setSelectedTileObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [pagination, setPagination] = useState({ totalPages: 1, totalItems: 0 });
  const [imagePreview, setImagePreview] = useState(null);
  const [showTileModal, setShowTileModal] = useState(false);
  const [viewProduct, setViewProduct] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedProductForQR, setSelectedProductForQR] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [expandedTiles, setExpandedTiles] = useState({});
  const [exportingQRCodes, setExportingQRCodes] = useState(false);

  // Form state
  const [form, setForm] = useState({
    name: '',
    productCode: '',
    brand: '',
    description: '',
    status: 'active',
    tiles: [],
    image: null,
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Fetch products
  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit, search, status: filterStatus, brand: filterBrand };
      const res = await axios.get(`${API}/concept-products`, { headers, params });
      if (res.data.success) {
        setProducts(res.data.data.conceptProducts);
        setPagination(res.data.data.pagination);
      }
    } catch (err) {
      console.error('Fetch products error:', err);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, filterStatus, filterBrand]);

  // Fetch brands
  const fetchBrands = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/concept-products/brands/all`, { headers });
      if (res.data.success) {
        setBrands(res.data.brands.map(b => ({ value: b._id, label: b.name })));
      }
    } catch (err) {
      console.error('Fetch brands error:', err);
    }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  useEffect(() => { fetchBrands(); }, [fetchBrands]);

  // Form helpers
  const resetForm = () => {
    setForm({ name: '', productCode: '', brand: '', description: '', status: 'active', tiles: [], image: null });
    setSelectedTileObjects([]);
    setImagePreview(null);
    setIsEditing(false);
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (product) => {
    const tileIds = (product.tiles || []).map(t => t._id || t);
    setForm({
      name: product.name || '',
      productCode: product.productCode || '',
      brand: product.brand?._id || product.brand || '',
      description: product.description || '',
      status: product.status || 'active',
      tiles: tileIds,
      image: null,
    });
    setSelectedTileObjects((product.tiles || []).map(t => ({
      _id: t._id || t,
      tilesName: t.displayName || t.tilesName || 'Tile',
      id: t.id || '',
      partNumber: t.partNumber || '',
    })));
    setImagePreview(product.imageId ? `${API}/concept-products/image/${product._id}?${Date.now()}` : null);
    setIsEditing(true);
    setEditingId(product._id);
    setShowForm(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        Swal.fire('Error', 'Image must be less than 10MB', 'error');
        return;
      }
      setForm(prev => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      Swal.fire('Error', 'Product name is required', 'error');
      return;
    }
    if (form.tiles.length === 0) {
      Swal.fire('Error', 'Select at least one tile', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('name', form.name.trim());
      formData.append('productCode', form.productCode.trim());
      formData.append('description', form.description.trim());
      formData.append('status', form.status);
      formData.append('tiles', JSON.stringify(form.tiles));
      if (form.brand) formData.append('brand', form.brand);
      if (form.image) formData.append('image', form.image);

      let res;
      if (isEditing) {
        res = await axios.put(`${API}/concept-products/${editingId}`, formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        });
      } else {
        res = await axios.post(`${API}/concept-products`, formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        });
      }

      if (res.data.success) {
        Swal.fire({ icon: 'success', title: 'Success', text: res.data.message, timer: 1500, showConfirmButton: false });
        resetForm();
        fetchProducts();
      }
    } catch (err) {
      console.error('Submit error:', err);
      Swal.fire('Error', err.response?.data?.message || 'Failed to save concept product', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Concept Product?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'Delete',
    });
    if (!result.isConfirmed) return;
    try {
      const res = await axios.delete(`${API}/concept-products/${id}`, { headers });
      if (res.data.success) {
        Swal.fire({ icon: 'success', title: 'Deleted', timer: 1200, showConfirmButton: false });
        fetchProducts();
      }
    } catch (err) {
      Swal.fire('Error', 'Failed to delete', 'error');
    }
  };

  // Tile selection helpers
  const handleTileSelect = (tile) => {
    if (!form.tiles.includes(tile._id)) {
      setForm(prev => ({ ...prev, tiles: [...prev.tiles, tile._id] }));
      setSelectedTileObjects(prev => [...prev, tile]);
    }
    setShowTileModal(false);
  };

  // View product details with stock
  const openViewProduct = async (product) => {
    setViewProduct(product);
    setExpandedTiles({});
    setViewLoading(true);
    try {
      const res = await axios.get(`${API}/concept-products/${product._id}`, { headers });
      if (res.data.success) {
        setViewProduct(res.data.data);
      }
    } catch (err) {
      console.error('Fetch product details error:', err);
    } finally {
      setViewLoading(false);
    }
  };

  const toggleTileStock = (tileId) => {
    setExpandedTiles(prev => ({ ...prev, [tileId]: !prev[tileId] }));
  };

  const removeTile = (tileId) => {
    setForm(prev => ({ ...prev, tiles: prev.tiles.filter(t => t !== tileId) }));
    setSelectedTileObjects(prev => prev.filter(t => t._id !== tileId));
  };

  // QR Code
  const handleGenerateQR = async (product) => {
    try {
      const dataUrl = await QRCode.toDataURL(product._id, {
        width: 300,
        margin: 2,
        color: { dark: '#000000', light: '#FFFFFF' }
      });
      setQrCodeUrl(dataUrl);
      setSelectedProductForQR(product);
      setShowQRModal(true);
    } catch (error) {
      console.error('Error generating QR code:', error);
      Swal.fire('Error', 'Failed to generate QR code', 'error');
    }
  };

  const handleDownloadQRPng = () => {
    if (!qrCodeUrl || !selectedProductForQR) return;
    const link = document.createElement('a');
    link.download = `concept-product-${selectedProductForQR.name || selectedProductForQR._id}-qr.png`;
    link.href = qrCodeUrl;
    link.click();
  };

  const handleDownloadQRPdf = (includeName = false) => {
    const svg = document.getElementById('cp-qr-code-svg');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const pngDataUrl = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [45, 65] });
      if (includeName) {
        const nameText = selectedProductForQR?.name || 'Concept Product';
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        const nameWidth = pdf.getTextWidth(nameText);
        pdf.text(nameText, Math.max((45 - nameWidth) / 2, 2), 8);
        pdf.addImage(pngDataUrl, 'PNG', 7.5, 12, 30, 30);
        const idText = selectedProductForQR?.id || '';
        if (idText) {
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'normal');
          const idWidth = pdf.getTextWidth(idText);
          pdf.text(idText, (45 - idWidth) / 2, 46);
        }
      } else {
        pdf.addImage(pngDataUrl, 'PNG', 5, 15, 35, 35);
      }
      const suffix = includeName ? '_QR_with_name' : '_QR';
      pdf.save(`${selectedProductForQR?.name || 'ConceptProduct'}${suffix}.pdf`);
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const handleExportAllQRCodes = async () => {
    setExportingQRCodes(true);

    try {
      // Show progress
      Swal.fire({
        title: 'Exporting QR Codes',
        text: 'Fetching all products...',
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Fetch all products (not just current page)
      const allProducts = [];
      let currentPage = 1;
      let hasMorePages = true;

      while (hasMorePages) {
        try {
          const params = {
            page: currentPage,
            limit: 100, // Fetch 100 products per page for efficiency
            search: '', // Don't apply search filter for export
            status: filterStatus, // Keep status filter
            brand: filterBrand // Keep brand filter
          };
          const res = await axios.get(`${API}/concept-products`, { headers, params });

          if (res.data.success && res.data.data.conceptProducts.length > 0) {
            allProducts.push(...res.data.data.conceptProducts);
            const pagination = res.data.data.pagination;

            if (currentPage >= pagination.totalPages) {
              hasMorePages = false;
            } else {
              currentPage++;
            }
          } else {
            hasMorePages = false;
          }
        } catch (error) {
          console.error(`Error fetching page ${currentPage}:`, error);
          hasMorePages = false;
        }
      }

      if (allProducts.length === 0) {
        Swal.close();
        Swal.fire('No Products', 'There are no products to export QR codes for.', 'info');
        return;
      }

      // Update progress
      Swal.update({
        text: `Generating QR codes for ${allProducts.length} products...`
      });

      const zip = new JSZip();
      let successCount = 0;
      let errorCount = 0;

      // Process all products
      for (const product of allProducts) {
        try {
          // Generate QR code for this product
          const dataUrl = await QRCode.toDataURL(product._id, {
            width: 300,
            margin: 2,
            color: { dark: '#000000', light: '#FFFFFF' }
          });

          // Convert data URL to blob
          const response = await fetch(dataUrl);
          const blob = await response.blob();

          // Create filename
          const fileName = `concept-product-${product.name || product.productCode || product._id}-qr.png`
            .replace(/[^a-zA-Z0-9\-_.]/g, '_'); // Sanitize filename

          // Add to zip
          zip.file(fileName, blob);
          successCount++;

        } catch (error) {
          console.error(`Error generating QR for product ${product._id}:`, error);
          errorCount++;
        }
      }

      // Generate and download zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const timestamp = new Date().toISOString().split('T')[0];
      saveAs(zipBlob, `concept-products-qr-codes-${timestamp}.zip`);

      // Close loading and show success
      Swal.close();
      Swal.fire({
        title: 'Export Complete',
        text: `Successfully exported ${successCount} QR codes${errorCount > 0 ? ` (${errorCount} failed)` : ''}`,
        icon: errorCount > 0 ? 'warning' : 'success',
        confirmButtonText: 'OK'
      });

    } catch (error) {
      console.error('Error exporting QR codes:', error);
      Swal.close();
      Swal.fire('Export Failed', 'An error occurred while exporting QR codes.', 'error');
    } finally {
      setExportingQRCodes(false);
    }
  };

  // Styles
  const bg = darkMode ? 'bg-gray-900' : 'bg-gray-50';
  const cardBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500';
  const border = darkMode ? 'border-gray-700' : 'border-gray-200';
  const inputBg = darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300';
  const hoverBg = darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50';
  const headerGradient = darkMode ? 'bg-gradient-to-r from-gray-800 to-gray-700' : 'bg-gradient-to-r from-blue-50 to-indigo-50';
  const cardHoverBorder = darkMode ? 'hover:border-blue-500' : 'hover:border-blue-400';
  const subtleBg = darkMode ? 'bg-gray-800/50' : 'bg-gray-50';

  const selectStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: darkMode ? '#374151' : '#fff',
      borderColor: darkMode ? '#4B5563' : '#D1D5DB',
      color: darkMode ? '#fff' : '#111',
      minHeight: '42px',
    }),
    menu: (base) => ({ ...base, backgroundColor: darkMode ? '#374151' : '#fff', zIndex: 50 }),
    option: (base, s) => ({
      ...base,
      backgroundColor: s.isFocused ? (darkMode ? '#4B5563' : '#E5E7EB') : 'transparent',
      color: darkMode ? '#fff' : '#111',
    }),
    singleValue: (base) => ({ ...base, color: darkMode ? '#fff' : '#111' }),
    input: (base) => ({ ...base, color: darkMode ? '#fff' : '#111' }),
    placeholder: (base) => ({ ...base, color: darkMode ? '#9CA3AF' : '#6B7280' }),
  };

  return (
    <div className={`min-h-screen ${bg} p-4 md:p-6`}>
      {/* Header */}
      <div className={`${headerGradient} rounded-xl p-5 mb-6 border ${border}`}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className={`text-2xl font-bold ${textPrimary}`}>Concept Products</h1>
            <p className={`text-sm ${textSecondary} mt-0.5`}>Manage concept product combinations with tiles</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleExportAllQRCodes}
              disabled={exportingQRCodes}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 disabled:from-gray-400 disabled:to-gray-500 transition-all duration-200 font-medium shadow-sm disabled:cursor-not-allowed"
              title="Export QR codes for ALL products (not just current page) as PNG files in a ZIP archive"
            >
              {exportingQRCodes ? (
                <FaSpinner className="w-4 h-4 animate-spin" />
              ) : (
                <FaDownload className="w-4 h-4" />
              )}
              {exportingQRCodes ? 'Exporting...' : 'Export All QR Codes'}
            </button>
            <button
              onClick={() => { resetForm(); setShowForm(true); }}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-medium shadow-sm"
            >
              <FaPlus className="w-4 h-4" /> New Concept Product
            </button>
          </div>
        </div>
        {!loading && (
          <div className="flex flex-wrap gap-3 mt-4">
            <span className={`px-3 py-1.5 rounded-lg text-sm font-medium shadow-sm ${darkMode ? 'bg-blue-900/30 text-blue-400 border border-blue-800' : 'bg-blue-100 text-blue-700 border border-blue-200'}`}>
              {pagination.totalItems || products.length} Total
            </span>
            <span className={`px-3 py-1.5 rounded-lg text-sm font-medium shadow-sm ${darkMode ? 'bg-green-900/30 text-green-400 border border-green-800' : 'bg-green-100 text-green-700 border border-green-200'}`}>
              {products.filter(p => p.status === 'active').length} Active
            </span>
            <span className={`px-3 py-1.5 rounded-lg text-sm font-medium shadow-sm ${darkMode ? 'bg-red-900/30 text-red-400 border border-red-800' : 'bg-red-100 text-red-700 border border-red-200'}`}>
              {products.filter(p => p.status === 'inactive').length} Inactive
            </span>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className={`${cardBg} rounded-xl border ${border} p-4 mb-6`}>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <FaSearch className={`absolute left-3 top-1/2 -translate-y-1/2 ${textSecondary}`} />
            <input
              type="text"
              placeholder="Search by name, code, description..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className={`w-full pl-10 pr-4 py-2.5 rounded-lg border ${inputBg} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
            className={`px-3 py-2.5 rounded-lg border ${inputBg}`}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <select
            value={filterBrand}
            onChange={(e) => { setFilterBrand(e.target.value); setPage(1); }}
            className={`px-3 py-2.5 rounded-lg border ${inputBg} min-w-[150px]`}
          >
            <option value="">All Brands</option>
            {brands.map(b => (
              <option key={b.value} value={b.value}>{b.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Product List */}
      <div className={`${cardBg} rounded-xl border ${border} overflow-hidden`}>
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <FaSpinner className="animate-spin w-8 h-8 text-blue-500" />
          </div>
        ) : products.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <FaBoxOpen className={`w-16 h-16 mb-4 ${textSecondary}`} />
            <p className={`text-lg font-medium ${textSecondary}`}>No concept products found</p>
            <p className={`text-sm ${textSecondary} mt-1`}>Create your first concept product to get started</p>
          </div>
        ) : (
          <>
            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 p-4">
              {products.map(p => (
                <div key={p._id} className={`${cardBg} border-2 ${border} rounded-xl overflow-hidden transition-all duration-300 hover:shadow-2xl ${cardHoverBorder}`}>
                  {/* Image */}
                  <div className={`relative h-48 overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gradient-to-br from-gray-100 to-gray-200'}`}>
                    {p.imageId ? (
                      <img
                        src={`${API}/concept-products/image/${p._id}?v=${p.updatedAt || ''}`}
                        alt={p.name}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                      />
                    ) : null}
                    <div className={`${p.imageId ? 'hidden' : 'flex'} w-full h-full items-center justify-center`}>
                      <FaImage className={`w-12 h-12 ${textSecondary} opacity-40`} />
                    </div>
                    {/* Status badge */}
                    <span className={`absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm backdrop-blur-sm ${
                      p.status === 'active'
                        ? (darkMode ? 'bg-green-900/60 text-green-400 border border-green-700' : 'bg-green-100 text-green-800')
                        : (darkMode ? 'bg-red-900/60 text-red-400 border border-red-700' : 'bg-red-100 text-red-800')
                    }`}>
                      {p.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                    {/* ID badge */}
                    {p.id && (
                      <span className="absolute top-3 left-3 px-2 py-1 rounded-md text-xs font-mono font-semibold bg-black/50 text-white backdrop-blur-sm">
                        {p.id}
                      </span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <h3 className={`text-lg font-semibold ${textPrimary} truncate`}>{p.name}</h3>
                    {p.brand?.name && (
                      <div className={`flex items-center gap-1.5 mt-1 ${textSecondary} text-sm`}>
                        <FaTags className="w-3 h-3" /> {p.brand.name}
                      </div>
                    )}
                    {p.productCode && (
                      <div className={`flex items-center gap-1.5 mt-1 ${textSecondary} text-sm`}>
                        <FaBarcode className="w-3 h-3" /> {p.productCode}
                      </div>
                    )}
                    {p.description && (
                      <p className={`mt-2 text-sm ${textSecondary} line-clamp-2`}>{p.description}</p>
                    )}

                    {/* Tiles count */}
                    <div className={`mt-3 flex items-center gap-1.5 text-sm ${textSecondary}`}>
                      <FaBoxOpen className="w-3.5 h-3.5" />
                      <span>{p.tiles?.length || 0} tile{(p.tiles?.length || 0) !== 1 ? 's' : ''}</span>
                    </div>

                    {/* Tiles chips */}
                    {p.tiles && p.tiles.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {p.tiles.slice(0, 3).map(t => (
                          <span key={t._id || t} className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-700'} truncate max-w-[150px]`}>
                            {t.id || t.displayName || t.tilesName || 'Tile'}
                          </span>
                        ))}
                        {p.tiles.length > 3 && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                            +{p.tiles.length - 3} more
                          </span>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className={`mt-4 flex items-center gap-2 pt-3 border-t ${border}`}>
                      <button
                        onClick={() => openViewProduct(p)}
                        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} transition-colors`}
                      >
                        <FaInfoCircle className="w-3.5 h-3.5" /> Details
                      </button>
                      <button
                        onClick={() => handleGenerateQR(p)}
                        className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${darkMode ? 'bg-purple-900/30 text-purple-400 hover:bg-purple-900/50' : 'bg-purple-50 text-purple-700 hover:bg-purple-100'}`}
                        title="Generate QR Code"
                      >
                        <FaQrcode className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleEdit(p)}
                        className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${darkMode ? 'bg-blue-900/30 text-blue-400 hover:bg-blue-900/50' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
                      >
                        <FaEdit className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(p._id)}
                        className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${darkMode ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50' : 'bg-red-50 text-red-700 hover:bg-red-100'}`}
                      >
                        <FaTrash className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className={`flex items-center justify-between px-4 py-3 border-t ${border}`}>
                <span className={`text-sm ${textSecondary}`}>
                  Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalItems} total)
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={`p-2 rounded-lg border ${border} ${hoverBg} disabled:opacity-40 ${textPrimary}`}
                  >
                    <FaChevronLeft />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                    disabled={page === pagination.totalPages}
                    className={`p-2 rounded-lg border ${border} ${hoverBg} disabled:opacity-40 ${textPrimary}`}
                  >
                    <FaChevronRight />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail View Modal */}
      {viewProduct && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setViewProduct(null)}>
          <div className={`${cardBg} rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl`} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className={`flex items-center justify-between p-5 border-b ${border} ${headerGradient}`}>
              <div>
                <h2 className={`text-xl font-bold ${textPrimary}`}>{viewProduct.name}</h2>
                {viewProduct.id && <span className={`text-sm font-mono ${textSecondary}`}>{viewProduct.id}</span>}
              </div>
              <button onClick={() => setViewProduct(null)} className={`p-2 rounded-lg ${hoverBg} ${textSecondary}`}>
                <FaTimes />
              </button>
            </div>

            {/* Image */}
            {viewProduct.imageId && (
              <div className={`w-full h-64 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <img
                  src={`${API}/concept-products/image/${viewProduct._id}?v=${viewProduct.updatedAt || ''}`}
                  alt={viewProduct.name}
                  className="w-full h-full object-contain"
                />
              </div>
            )}

            {/* Details */}
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {viewProduct.brand?.name && (
                  <div>
                    <span className={`text-xs font-medium uppercase ${textSecondary}`}>Brand</span>
                    <p className={`mt-0.5 font-medium ${textPrimary}`}>{viewProduct.brand.name}</p>
                  </div>
                )}
                {viewProduct.productCode && (
                  <div>
                    <span className={`text-xs font-medium uppercase ${textSecondary}`}>Product Code</span>
                    <p className={`mt-0.5 font-medium ${textPrimary}`}>{viewProduct.productCode}</p>
                  </div>
                )}
                <div>
                  <span className={`text-xs font-medium uppercase ${textSecondary}`}>Status</span>
                  <p className="mt-0.5">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                      viewProduct.status === 'active'
                        ? (darkMode ? 'bg-green-900/50 text-green-400 border border-green-700' : 'bg-green-100 text-green-800')
                        : (darkMode ? 'bg-red-900/50 text-red-400 border border-red-700' : 'bg-red-100 text-red-800')
                    }`}>
                      {viewProduct.status === 'active' ? <FaCheck className="w-2.5 h-2.5" /> : null}
                      {viewProduct.status}
                    </span>
                  </p>
                </div>
                {viewProduct.createdBy?.name && (
                  <div>
                    <span className={`text-xs font-medium uppercase ${textSecondary}`}>Created By</span>
                    <p className={`mt-0.5 font-medium ${textPrimary}`}>{viewProduct.createdBy.name}</p>
                  </div>
                )}
              </div>

              {viewProduct.description && (
                <div>
                  <span className={`text-xs font-medium uppercase ${textSecondary}`}>Description</span>
                  <p className={`mt-1 text-sm ${textPrimary}`}>{viewProduct.description}</p>
                </div>
              )}

              {/* Tiles list with stock */}
              <div>
                <span className={`text-xs font-medium uppercase ${textSecondary}`}>Tiles ({viewProduct.tiles?.length || 0})</span>
                {viewLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <FaSpinner className="animate-spin w-5 h-5 text-blue-500" />
                    <span className={`ml-2 text-sm ${textSecondary}`}>Loading stock data...</span>
                  </div>
                ) : (
                  <div className="mt-2 space-y-2">
                    {(viewProduct.tiles || []).map(t => {
                      const tileId = t._id || t;
                      const isExpanded = expandedTiles[tileId];
                      const hasStock = t.stock && t.stock.length > 0;
                      const totalFreshBox = hasStock ? t.stock.reduce((s, e) => s + (e.freshStockBox || 0), 0) : 0;
                      const totalFreshPcs = hasStock ? t.stock.reduce((s, e) => s + (e.freshStockPieces || 0), 0) : 0;
                      const totalDmgBox = hasStock ? t.stock.reduce((s, e) => s + (e.damagedStockBox || 0) + (e.cornerDamagedBox || 0), 0) : 0;
                      const totalDmgPcs = hasStock ? t.stock.reduce((s, e) => s + (e.damagedStockPieces || 0) + (e.cornerDamagedPieces || 0), 0) : 0;
                      return (
                        <div key={tileId} className={`rounded-lg border ${border} overflow-hidden`}>
                          {/* Tile header row */}
                          <div
                            className={`flex items-center gap-3 p-2.5 cursor-pointer ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} transition-colors`}
                            onClick={() => hasStock && toggleTileStock(tileId)}
                          >
                            <div className="w-10 h-10 flex-shrink-0">
                              <img
                                src={`${API}/tiles/image/${tileId}`}
                                alt=""
                                className={`w-10 h-10 object-cover rounded-lg border ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}
                                onError={(e) => { e.target.style.display = 'none'; if(e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                              />
                              <div className={`w-10 h-10 rounded-lg items-center justify-center border ${darkMode ? 'bg-gray-700 text-gray-400 border-gray-600' : 'bg-gray-100 text-gray-400 border-gray-200'}`} style={{ display: 'none' }}>
                                <FaBox size={14} />
                              </div>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className={`text-sm font-medium ${textPrimary} truncate`}>{t.displayName || t.tilesName || 'Tile'}</p>
                              <p className={`text-xs ${textSecondary}`}>
                                {t.id}{t.partNumber ? ` • ${t.partNumber}` : ''}
                                {t.brand?.name ? ` • ${t.brand.name}` : ''}
                              </p>
                              {(t.tilesMM?.parameterName || t.tilesCategory?.parameterName) && (
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                  {t.tilesMM?.parameterName && <span className={`text-[10px] px-1.5 py-0.5 rounded ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>{t.tilesMM.parameterName}</span>}
                                  {t.tilesCategory?.parameterName && <span className={`text-[10px] px-1.5 py-0.5 rounded ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>{t.tilesCategory.parameterName}</span>}
                                  {t.tilesFinishType?.parameterName && <span className={`text-[10px] px-1.5 py-0.5 rounded ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>{t.tilesFinishType.parameterName}</span>}
                                  {t.tilesType?.parameterName && <span className={`text-[10px] px-1.5 py-0.5 rounded ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>{t.tilesType.parameterName}</span>}
                                </div>
                              )}
                            </div>
                            {/* Stock summary */}
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {hasStock ? (
                                <>
                                  <div className="text-right">
                                    <p className={`text-xs font-bold ${totalFreshBox > 0 ? (darkMode ? 'text-green-400' : 'text-green-700') : (darkMode ? 'text-red-400' : 'text-red-600')}`}>
                                      {totalFreshBox} Box{totalFreshPcs > 0 ? ` + ${totalFreshPcs} Pcs` : ''}
                                    </p>
                                    {totalDmgBox > 0 && (
                                      <p className={`text-[10px] ${darkMode ? 'text-red-400' : 'text-red-500'}`}>
                                        {totalDmgBox} Dmg{totalDmgPcs > 0 ? ` + ${totalDmgPcs} Pcs` : ''}
                                      </p>
                                    )}
                                  </div>
                                  {isExpanded ? <FaChevronUp className={`w-3 h-3 ${textSecondary}`} /> : <FaChevronDown className={`w-3 h-3 ${textSecondary}`} />}
                                </>
                              ) : (
                                <span className={`text-xs ${textSecondary}`}>No stock</span>
                              )}
                            </div>
                          </div>

                          {/* Expanded stock by location */}
                          {isExpanded && hasStock && (
                            <div className={`border-t ${border} ${subtleBg}`}>
                              <div className="p-2.5 space-y-1.5">
                                {t.stock.map((entry, idx) => {
                                  const locName = entry.location?.locationName || entry.location?.name || 'Unknown';
                                  const fresh = entry.freshStockBox || 0;
                                  const freshPcs = entry.freshStockPieces || 0;
                                  const damaged = (entry.damagedStockBox || 0) + (entry.cornerDamagedBox || 0);
                                  const damagedPcs = (entry.damagedStockPieces || 0) + (entry.cornerDamagedPieces || 0);
                                  return (
                                    <div key={idx} className={`flex items-center justify-between px-3 py-2 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'} border ${border}`}>
                                      <div className="flex items-center gap-2">
                                        <FaWarehouse className={`w-3 h-3 ${textSecondary}`} />
                                        <span className={`text-xs font-medium ${textPrimary}`}>{locName}</span>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <div className="text-right">
                                          <span className={`text-xs font-semibold ${fresh > 0 ? (darkMode ? 'text-green-400' : 'text-green-700') : (darkMode ? 'text-gray-500' : 'text-gray-400')}`}>
                                            {fresh} Box{freshPcs > 0 ? ` + ${freshPcs} Pcs` : ''}
                                          </span>
                                        </div>
                                        {damaged > 0 && (
                                          <span className={`text-xs ${darkMode ? 'text-red-400' : 'text-red-500'}`}>
                                            {damaged} Dmg{damagedPcs > 0 ? ` + ${damagedPcs} Pcs` : ''}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={resetForm}>
          <div className={`${cardBg} rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl`} onClick={e => e.stopPropagation()}>
            {/* Form Header */}
            <div className={`flex items-center justify-between p-5 border-b ${border}`}>
              <h2 className={`text-xl font-bold ${textPrimary}`}>
                {isEditing ? 'Edit Concept Product' : 'New Concept Product'}
              </h2>
              <button onClick={resetForm} className={`p-2 rounded-lg ${hoverBg} ${textSecondary}`}>
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-5">
              {/* Image Upload */}
              <div>
                <label className={`block text-sm font-medium ${textPrimary} mb-2`}>Product Image</label>
                <div className="flex items-start gap-4">
                  <div className={`w-32 h-32 border-2 border-dashed ${border} rounded-xl overflow-hidden flex items-center justify-center ${inputBg} flex-shrink-0`}>
                    {imagePreview ? (
                      <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center">
                        <FaImage className={`w-8 h-8 mx-auto ${textSecondary} opacity-40`} />
                        <span className={`text-xs ${textSecondary} mt-1 block`}>No image</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <label className="block">
                      <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                      <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer text-sm font-medium transition-colors">
                        <FaImage className="w-4 h-4" /> {imagePreview ? 'Change Image' : 'Upload Image'}
                      </span>
                    </label>
                    <p className={`mt-2 text-xs ${textSecondary}`}>JPEG, PNG, WebP. Max 10MB. Duplicate images are auto-detected.</p>
                    {imagePreview && (
                      <button
                        type="button"
                        onClick={() => { setImagePreview(null); setForm(p => ({ ...p, image: null })); }}
                        className="mt-2 text-xs text-red-500 hover:text-red-700"
                      >
                        Remove image
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Name + Code */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium ${textPrimary} mb-1.5`}>
                    Product Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="Enter product name"
                    className={`w-full px-3 py-2.5 rounded-lg border ${inputBg} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    required
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${textPrimary} mb-1.5`}>Product Code</label>
                  <input
                    type="text"
                    value={form.productCode}
                    onChange={(e) => setForm(p => ({ ...p, productCode: e.target.value }))}
                    placeholder="e.g. CP-001"
                    className={`w-full px-3 py-2.5 rounded-lg border ${inputBg} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  />
                </div>
              </div>

              {/* Brand + Status */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium ${textPrimary} mb-1.5`}>Brand</label>
                  <Select
                    options={brands}
                    value={brands.find(b => b.value === form.brand) || null}
                    onChange={(opt) => setForm(p => ({ ...p, brand: opt?.value || '' }))}
                    isClearable
                    placeholder="Select brand..."
                    styles={selectStyles}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${textPrimary} mb-1.5`}>Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm(p => ({ ...p, status: e.target.value }))}
                    className={`w-full px-3 py-2.5 rounded-lg border ${inputBg}`}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className={`block text-sm font-medium ${textPrimary} mb-1.5`}>Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Describe the concept product..."
                  rows={3}
                  className={`w-full px-3 py-2.5 rounded-lg border ${inputBg} focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none`}
                />
              </div>

              {/* Tiles Selection */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className={`text-sm font-medium ${textPrimary}`}>
                    Tiles <span className="text-red-500">*</span>
                    <span className={`ml-2 text-xs ${textSecondary}`}>({form.tiles.length} selected)</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowTileModal(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
                  >
                    <FaPlus className="w-3 h-3" /> Add Tile
                  </button>
                </div>

                {/* Selected tiles */}
                {form.tiles.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {form.tiles.map((tileId, idx) => {
                      const tileObj = selectedTileObjects.find(t => t._id === tileId);
                      return (
                        <div key={tileId} className={`flex items-center justify-between p-2.5 rounded-lg border ${border} ${subtleBg}`}>
                          <div className="flex items-center gap-3 min-w-0">
                            <span className={`text-xs font-mono ${textSecondary} w-5 text-center flex-shrink-0`}>{idx + 1}</span>
                            <div className="w-10 h-10 flex-shrink-0">
                              <img
                                src={`${API}/tiles/image/${tileId}`}
                                alt=""
                                className={`w-10 h-10 object-cover rounded-lg border ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}
                                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                              />
                              <div className={`w-10 h-10 rounded-lg items-center justify-center border ${darkMode ? 'bg-gray-700 text-gray-400 border-gray-600' : 'bg-gray-100 text-gray-400 border-gray-200'}`} style={{ display: 'none' }}>
                                <FaBox size={14} />
                              </div>
                            </div>
                            <div className="min-w-0">
                              <p className={`text-sm font-medium ${textPrimary} truncate`}>{tileObj?.tilesName || tileObj?.displayName || 'Tile'}</p>
                              <p className={`text-xs ${textSecondary}`}>{tileObj?.id || ''} {tileObj?.partNumber ? `• ${tileObj.partNumber}` : ''}</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeTile(tileId)}
                            className={`p-1.5 rounded-md text-red-500 transition-colors flex-shrink-0 ${darkMode ? 'hover:bg-red-900/30' : 'hover:bg-red-50'}`}
                          >
                            <FaTimes className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className={`text-center py-6 rounded-lg border-2 border-dashed ${border}`}>
                    <FaBoxOpen className={`w-8 h-8 mx-auto ${textSecondary} opacity-40`} />
                    <p className={`mt-2 text-sm ${textSecondary}`}>No tiles selected. Click "Add Tile" to begin.</p>
                  </div>
                )}
              </div>

              {/* Submit */}
              <div className={`flex justify-end gap-3 pt-4 border-t ${border}`}>
                <button
                  type="button"
                  onClick={resetForm}
                  className={`px-5 py-2.5 rounded-lg border ${border} ${textPrimary} ${hoverBg} font-medium text-sm`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium text-sm transition-colors"
                >
                  {submitting ? (
                    <><FaSpinner className="animate-spin w-4 h-4" /> Saving...</>
                  ) : (
                    <><FaCheck className="w-4 h-4" /> {isEditing ? 'Update' : 'Create'}</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && selectedProductForQR && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowQRModal(false)}>
          <div className={`${cardBg} rounded-2xl max-w-md w-full shadow-2xl`} onClick={e => e.stopPropagation()}>
            <div className={`flex items-center justify-between p-5 border-b ${border}`}>
              <h3 className={`text-lg font-semibold ${textPrimary}`}>QR Code</h3>
              <button onClick={() => setShowQRModal(false)} className={`p-2 rounded-lg ${hoverBg} ${textSecondary}`}>
                <FaTimes />
              </button>
            </div>

            {/* Hidden SVG for PDF generation */}
            <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
              <QRCodeSVG id="cp-qr-code-svg" value={selectedProductForQR._id} size={200} bgColor="#ffffff" fgColor="#000000" />
            </div>

            <div className="p-5 space-y-4">
              <div className="text-center">
                <p className={`text-sm font-semibold ${textPrimary} mb-1`}>{selectedProductForQR.name}</p>
                {selectedProductForQR.id && <p className={`text-xs font-mono ${textSecondary}`}>{selectedProductForQR.id}</p>}
              </div>

              <div className={`p-4 rounded-xl border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex flex-col items-center space-y-3">
                  <div className="bg-white p-4 rounded-lg">
                    <img src={qrCodeUrl} alt="QR Code" className="w-56 h-56" />
                  </div>
                  <p className={`text-xs text-center break-all ${textSecondary}`}>ID: {selectedProductForQR._id}</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    <button
                      onClick={handleDownloadQRPng}
                      className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-colors text-sm"
                    >
                      <FaDownload className="w-4 h-4" /> PNG
                    </button>
                    <button
                      onClick={() => handleDownloadQRPdf(false)}
                      className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2 transition-colors text-sm"
                    >
                      <FaFilePdf className="w-4 h-4" /> PDF
                    </button>
                    <button
                      onClick={() => handleDownloadQRPdf(true)}
                      className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-colors text-sm"
                    >
                      <FaFilePdf className="w-4 h-4" /> PDF + Name
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tile Selection Modal */}
      <TileSelectionModal
        isOpen={showTileModal}
        onClose={() => setShowTileModal(false)}
        onSelect={handleTileSelect}
        darkMode={darkMode}
        userId={user?.user?._id}
        filterByLocation={false}
        showNetStock={false}
      />
    </div>
  );
};

export default ConceptProductManagement;
